import { useId } from "react";
import FormError from "./FormError";

interface FormPhoneInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  error?: string;
  errorParams?: Record<string, string | number>;
  viewMode?: boolean;
  layout?: "stacked" | "inline";
  id?: string;
}

export default function FormPhoneInput({
  label,
  value,
  onChange,
  placeholder,
  className = "",
  disabled = false,
  required = false,
  readOnly = false,
  error,
  errorParams,
  id: providedId,
}: FormPhoneInputProps) {
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    inputValue = inputValue.replace(/[^\d+]/g, "");

    if (inputValue.includes("+")) {
      const digits = inputValue.replace(/\+/g, "");
      inputValue = "+" + digits;
    }

    onChange(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "Backspace" ||
      e.key === "Delete" ||
      e.key === "Tab" ||
      e.key === "Escape" ||
      e.key === "Enter" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "Home" ||
      e.key === "End"
    ) {
      return;
    }

    if (e.ctrlKey || e.metaKey) {
      return;
    }

    if (e.key === "+" && value.includes("+")) {
      e.preventDefault();
      return;
    }

    if (!/[\d+]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const inputElement = (
    <div className="relative">
      <input
        id={id}
        type="tel"
        autoComplete="tel"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || "+370..."}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        aria-required={required}
        className={`
            w-full py-2 text-sm bg-white border rounded-md
            focus:outline-none transition-colors duration-200
            px-3
            ${
              readOnly
                ? "border-gray-200 bg-gray-50 focus:border-gray-200 focus:ring-0 focus:outline-none cursor-default"
                : error
                  ? "border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500 cursor-text"
                  : "border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-primary focus:border-primary cursor-text"
            }
            ${disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : ""}
          `}
      />
    </div>
  );

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-black mb-2"
        >
          {label}
          {required && <span className="text-black">*</span>}
        </label>
      )}
      {inputElement}
      <FormError error={error} params={errorParams} />
    </div>
  );
}
